import React, { useMemo } from 'react'
import type { ColumnsType } from 'antd/es/table'
import { type ParsedRow, type RowFieldErrors } from './bulkUpload.helpers'
import type { RowPlan } from './bulkUploadPlan'
import {
  ClientNameCell,
  DueDayInputCell,
  PaymentTypeCell,
  PlanCell,
  RemoveRowCell,
  ToEmailsCell,
  type UploadClientOption,
} from './bulkUploadCells'

interface UseBulkUploadColumnsParams {
  clients: UploadClientOption[]
  fieldErrorsByRow: Map<number, RowFieldErrors>
  plansByRow: Map<number, RowPlan>
  onClientFix: (rowNum: number, clientName: string) => void
  onFieldChange: (rowNum: number, field: keyof ParsedRow, value: string) => void
  onRemoveRow: (rowNum: number) => void
}

/** The upload preview's columns. Every cell is editable in place. */
export const useBulkUploadColumns = ({
  clients,
  fieldErrorsByRow,
  plansByRow,
  onClientFix,
  onFieldChange,
  onRemoveRow,
}: UseBulkUploadColumnsParams): ColumnsType<ParsedRow> =>
  useMemo(
    () => [
      { title: '#', dataIndex: 'rowNum', width: 45 },
      {
        title: 'Change',
        key: 'plan',
        width: 120,
        render: (_: unknown, row: ParsedRow) => (
          <PlanCell row={row} plan={plansByRow.get(row.rowNum)} />
        ),
      },
      {
        title: 'Client Name',
        dataIndex: 'clientName',
        width: 260,
        render: (name: string, row: ParsedRow) => (
          <ClientNameCell
            name={name}
            row={row}
            clients={clients}
            onClientFix={onClientFix}
          />
        ),
      },
      {
        title: 'To Emails',
        dataIndex: 'toEmails',
        render: (value: string, row: ParsedRow) => (
          <ToEmailsCell value={value} row={row} onFieldChange={onFieldChange} />
        ),
      },
      {
        title: 'Payment Type',
        dataIndex: 'paymentType',
        width: 140,
        render: (value: string, row: ParsedRow) => (
          <PaymentTypeCell
            value={value}
            row={row}
            error={fieldErrorsByRow.get(row.rowNum)?.paymentType}
            onFieldChange={onFieldChange}
          />
        ),
      },
      {
        title: 'Due Day',
        dataIndex: 'dueDay',
        width: 110,
        render: (value: string, row: ParsedRow) => (
          <DueDayInputCell
            value={value}
            row={row}
            error={fieldErrorsByRow.get(row.rowNum)?.dueDay}
            onFieldChange={onFieldChange}
          />
        ),
      },
      {
        title: '',
        key: 'remove',
        width: 40,
        render: (_: unknown, row: ParsedRow) => (
          <RemoveRowCell row={row} onRemoveRow={onRemoveRow} />
        ),
      },
    ],
    [
      clients,
      fieldErrorsByRow,
      plansByRow,
      onClientFix,
      onFieldChange,
      onRemoveRow,
    ]
  )
